import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { LogOut, FileText, ArrowLeft } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
} from "../ui/Dialog";
import Button from "../ui/Button";
import TermsView from "./TermsView";
import TermsAcceptBar from "./TermsAcceptBar";
import { useTermsReader } from "./useTermsReader";
import {
  coerceKind,
  getPendingTerms,
  termsKindLabel,
  type PlatformTermsKind,
} from "../../api/General/PlatformTerms";
import { isPublicRoute } from "../../utils/publicRoutes";
import { logout as logoutApi } from "../../utils/AuthService";

/**
 * PendingTermsGate — mounted high in the tree under MasterLayout.
 *
 * After login, fetches the kinds of platform terms the user has not yet accepted
 * the current published version of. If non-empty, it pops a non-dismissible modal
 * that walks the user through each kind. Each step is gated on either
 * "scroll to bottom" OR clicking "I've read it" (both surfaced inside TermsView)
 * before the Accept button enables.
 *
 * Bypass paths:
 *   - Logout: dismiss the modal by logging the user out (we can't dismiss it
 *     any other way; signing out via the OIDC client is the user's only escape).
 */

const PendingTermsGate: React.FC = () => {
  const { t } = useTranslation();
  const isLoggedIn = useSelector(
    (state: { user?: { isLoggedIn?: boolean } }) => state.user?.isLoggedIn,
  );
  const userId = useSelector(
    (state: { user?: { userId?: string } }) => state.user?.userId,
  );

  const [pending, setPending] = useState<PlatformTermsKind[]>([]);
  const [cursor, setCursor] = useState(0);

  const isPublic = useMemo(
    () => typeof window !== "undefined" && isPublicRoute(window.location.pathname),
    [],
  );

  const fetchPending = useCallback(async () => {
    if (!isLoggedIn || isPublic) return;
    try {
      const res = await getPendingTerms();
      if (res.errors && res.errors.length > 0) {
        // Don't block the app on a transient failure: log only and continue.
        console.warn("[PendingTermsGate] pending fetch failed:", res.errors[0]);
        setPending([]);
      } else {
        const list = (res.payload || []).map((v) => coerceKind(v));
        // de-dup, keep stable order: GeneralUsage → Privacy → TrialUpgrade
        const order: PlatformTermsKind[] = ["GeneralUsage", "Privacy", "TrialUpgrade"];
        const set = new Set<PlatformTermsKind>(list);
        setPending(order.filter((k) => set.has(k)));
        setCursor(0);
      }
    } catch {
      setPending([]);
    }
  }, [isLoggedIn, isPublic]);

  useEffect(() => {
    fetchPending();
    // Refetch when user identity changes (login/switch user)
  }, [fetchPending, userId]);

  const advance = () => {
    setCursor((c) => {
      const next = c + 1;
      if (next >= pending.length) {
        setPending([]);
        return 0;
      }
      return next;
    });
  };

  const goBack = () => setCursor((c) => Math.max(0, c - 1));

  const logout = async () => {
    // Mirror UserMenu's deliberate sign-out: signoutRedirect navigates to the
    // backend IdP logout, which invalidates the session and redirects back to
    // /logoutcallback where local state is cleared.
    await logoutApi();
  };

  const open = pending.length > 0 && cursor < pending.length;
  const current = open ? pending[cursor] : null;
  const stepCount = pending.length;
  const stepIndex = cursor + 1;

  const reader = useTermsReader(current ?? "GeneralUsage", {
    autoAccept: true,
    onAccepted: advance,
    enabled: open,
  });

  if (!open || !current) return null;

  return (
    <Dialog open={open}>
      <DialogContent
        size="lg"
        hideCloseButton
        className="flex flex-col max-h-[92vh] overflow-hidden"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <div className="flex items-start gap-3">
            <div className="shrink-0 w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
              <FileText size={20} />
            </div>
            <div className="min-w-0 flex-1">
              <DialogTitle>{t("Updated terms require your acceptance")}</DialogTitle>
              <DialogDescription className="mt-1">
                {t("We've updated the documents below. Please review and accept to continue.")}
              </DialogDescription>
              <div className="mt-2 inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium tabular-nums bg-muted text-muted-foreground border border-border">
                {t("{{i}} of {{n}}", { i: stepIndex, n: stepCount })}
                <span className="mx-1.5 opacity-40">·</span>
                {current ? t(termsKindLabel(current)) : ""}
              </div>
            </div>
          </div>
        </DialogHeader>

        <DialogBody className="flex flex-col flex-1 min-h-0 overflow-hidden p-0">
          <TermsView key={current} reader={reader} kind={current} embedded hideHeader />
        </DialogBody>

        <DialogFooter className="flex-wrap items-center justify-end gap-x-3 gap-y-2">
          <div className="flex items-center gap-1 me-auto">
            <Button variant="ghost" onClick={logout} disabled={reader.submitting}>
              <LogOut size={14} className="me-2" />
              {t("Log out")}
            </Button>
            {cursor > 0 && (
              <Button
                variant="ghost"
                onClick={goBack}
                disabled={reader.submitting}
                aria-label={t("Go back to previous document")}
              >
                <ArrowLeft size={14} className="me-2" />
                {t("Back")}
              </Button>
            )}
          </div>
          <TermsAcceptBar reader={reader} />
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PendingTermsGate;
